import React from 'react';

var STATUS_ICON = { pending: '⏳', done: '✅', error: '❌', paused: '⏸' };

function DeploymentWindow(props) {
  var show         = props.show;
  var minimised    = props.minimised;
  var logs         = props.logs;
  var stages       = props.stages;
  var confirmation = props.confirmation;
  var tech         = props.tech;
  var apis         = props.apis || [];
  var pipelineStages = props.pipelineStages || [];
  var onMinimise   = props.onMinimise;
  var onClose      = props.onClose;
  var onProceed    = props.onProceed;
  var onCancel     = props.onCancel;
  var onRetry      = props.onRetry;

  var logsEndRef = React.useRef(null);
  // Per-stage collapse overrides (stage name -> bool). Default open = failed || last.
  var openStagesState = React.useState({});
  var openStages = openStagesState[0];
  var setOpenStages = openStagesState[1];

  React.useEffect(function() {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  if (!show) return null;

  var techLabel   = tech === 'mule' ? 'MuleSoft' : tech === 'springboot' ? 'Spring Boot' : tech === 'dotnet' ? '.NET' : 'Deployment';
  var isDone      = apis.length > 0
    ? apis.every(function(a) { return a.status === 'done' || a.status === 'error'; })
    : stages.length > 0 && stages.every(function(s) { return s.status === 'done' || s.status === 'error'; });
  var hasError    = apis.length > 0
    ? apis.some(function(a) { return a.status === 'error'; })
    : stages.some(function(s) { return s.status === 'error'; });
  var activeStage = stages.find(function(s) { return s.status === 'active'; });
  var activeIdx   = stages.findIndex(function(s) { return s.status === 'active'; });

  if (minimised) {
    return (
      <div className="sh-dw-statusbar">
        <span className="sh-dw-statusbar-label">
          {'🚀 ' + techLabel + ' · ' + (activeStage ? ('Stage ' + (activeIdx + 1) + '/' + stages.length + ': ' + activeStage.name) : (isDone ? 'Complete' : 'Starting…'))}
          {!isDone && !confirmation && <span className="sh-dw-running-dot" />}
        </span>
        <button className="sh-dw-statusbar-expand" onClick={onMinimise}>▲ Expand</button>
      </div>
    );
  }

  return (
    <div className="sh-deploy-window">
      <div className="sh-dw-titlebar">
        <span className="sh-dw-title">{'🚀 Deployment · ' + techLabel}</span>
        <span className={'sh-dw-status' + (hasError ? ' sh-dw-status--error' : isDone ? ' sh-dw-status--done' : confirmation ? ' sh-dw-status--paused' : '')}>
          {hasError ? '❌ Failed' : isDone ? '✅ Complete' : confirmation ? '⏸ Paused' : (
            <React.Fragment>
              <span className="sh-dw-running-dot" />
              {' Running'}
            </React.Fragment>
          )}
        </span>
        <div className="sh-dw-controls">
          <button className="sh-dw-btn" onClick={onMinimise} title="Minimise">−</button>
          <button className="sh-dw-btn" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {apis.length > 0 ? (
        <div className="sh-dw-api-cards">
          {apis.map(function(api, i) {
            return (
              <div key={i} className={'sh-dw-api-card sh-dw-api-card--' + api.status}>
                <div className="sh-dw-api-card-header">
                  <span className="sh-dw-api-card-name">{api.displayName || api.name}</span>
                  <span className="sh-dw-api-card-status">
                    {api.status === 'active'
                      ? <React.Fragment><span className="sh-dw-stage-spin sh-dw-stage-spin--sm" />{' deploying'}</React.Fragment>
                      : api.status === 'done'  ? '✅ Done'
                      : api.status === 'error' ? '❌ Failed'
                      : '⏳ Pending'}
                  </span>
                </div>
                {api.stages && api.stages.length > 0 && (
                  <div className="sh-dw-api-card-stages">
                    {api.stages.map(function(s, j) {
                      var cls = 'sh-dw-stage sh-dw-stage--' + s.status;
                      return (
                        <React.Fragment key={s.name}>
                          <div className={cls}>
                            <span className="sh-dw-stage-icon">
                              {s.status === 'active'
                                ? <span className="sh-dw-stage-spin" />
                                : (STATUS_ICON[s.status] || '⏳')}
                            </span>
                            <span className="sh-dw-stage-name">{s.name}</span>
                          </div>
                          {j < api.stages.length - 1 && <span className="sh-dw-stage-sep">›</span>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        stages.length > 0 && (
          <div className="sh-dw-stage-strip">
            {stages.map(function(s, i) {
              var cls = 'sh-dw-stage';
              if (s.status === 'active')  cls += ' sh-dw-stage--active';
              if (s.status === 'done')    cls += ' sh-dw-stage--done';
              if (s.status === 'error')   cls += ' sh-dw-stage--error';
              if (s.status === 'paused')  cls += ' sh-dw-stage--paused';
              return (
                <React.Fragment key={s.name}>
                  <div className={cls}>
                    <span className="sh-dw-stage-icon">
                      {s.status === 'active'
                        ? <span className="sh-dw-stage-spin" />
                        : (STATUS_ICON[s.status] || '⏳')}
                    </span>
                    <span className="sh-dw-stage-name">{s.name}</span>
                  </div>
                  {i < stages.length - 1 && <span className="sh-dw-stage-sep">›</span>}
                </React.Fragment>
              );
            })}
          </div>
        )
      )}

      {pipelineStages.length > 0 && (
        <div className="sh-dw-pipeline sh-dw-pstage-body" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '6px 0', flex: 1, minHeight: 0, overflowY: 'auto', scrollbarColor: 'rgba(255,255,255,0.12) transparent', scrollbarWidth: 'thin' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7a93bc', padding: '4px 12px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Pipeline stages
          </div>
          {pipelineStages.map(function(ps, idx) {
            var isLast = idx === pipelineStages.length - 1;
            // Status inference: only the most-recently-arrived stage is still running;
            // any earlier stage has finished (done) unless it explicitly failed.
            var stageState = ps.failed ? 'error' : (isDone ? 'done' : (isLast ? 'running' : 'done'));
            var open = Object.prototype.hasOwnProperty.call(openStages, ps.stage) ? openStages[ps.stage] : (ps.failed || (isLast && !isDone));
            return (
              <div key={ps.stage} style={{ margin: '2px 8px', border: '1px solid ' + (ps.failed ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'), borderRadius: 6, overflow: 'hidden', background: '#0d1117' }}>
                <button
                  onClick={function() { setOpenStages(function(o) { var n = Object.assign({}, o); n[ps.stage] = !open; return n; }); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', background: ps.failed ? 'rgba(239,68,68,0.08)' : '#161b22', border: 'none', color: ps.failed ? '#fca5a5' : '#dce8ff', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, fontWeight: 600 }}
                >
                  <span style={{ fontSize: 10, color: '#6a7a90' }}>{open ? '▾' : '▸'}</span>
                  <span>{stageState === 'error' ? '❌' : stageState === 'done' ? '✅' : <span className="sh-dw-stage-spin" />}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ps.stage}</span>
                  {ps.recordType && <span style={{ fontSize: 10, color: '#5a6b80', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, padding: '1px 6px' }}>{ps.recordType}</span>}
                  <span style={{ fontSize: 10.5, color: '#5a6b80', fontFamily: 'monospace' }}>{(ps.lines || []).length}</span>
                </button>
                {open && (
                  <div className="sh-dw-pstage-body" style={{ maxHeight: 220, overflowY: 'auto', padding: '6px 10px', fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.5, scrollbarColor: 'rgba(255,255,255,0.12) transparent', scrollbarWidth: 'thin' }}>
                    {(ps.lines || []).map(function(line, li) {
                      return (
                        <div key={li} style={{ color: ps.failed ? '#fca5a5' : '#c9d6e8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{line}</div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pipelineStages.length === 0 && (
      <div className="sh-dw-terminal">
        {logs.length === 0 && (
          <div className="sh-dw-log-line sh-dw-log--info">
            <span className="sh-dw-log-time">--:--:--</span>
            <span className="sh-dw-log-text">Starting deployment…</span>
          </div>
        )}
        {logs.map(function(l, i) {
          return (
            <div key={i} className={'sh-dw-log-line sh-dw-log--' + (l.type || 'info')}>
              <span className="sh-dw-log-time">{l.timestamp}</span>
              <span className="sh-dw-log-text">{l.text}</span>
            </div>
          );
        })}
        <div ref={logsEndRef} />
      </div>
      )}

      {confirmation && (
        <div className="sh-dw-confirmation">
          <div className="sh-deploy-confirmation-header">
            <span className="sh-deploy-pulse" />
            <span>Deployment Paused — Awaiting Your Verification</span>
          </div>
          <p className="sh-deploy-confirmation-msg">{confirmation.message}</p>
          {confirmation.details && (
            <div className="sh-deploy-confirmation-details">
              {confirmation.details.assetId    && <span>Asset: <strong>{confirmation.details.assetId}</strong></span>}
              {confirmation.details.instanceId && <span>Instance: <strong>{confirmation.details.instanceId}</strong></span>}
              {confirmation.details.cloudhubUrl && <span className="sh-deploy-cloudhub">CloudHub: <a href={confirmation.details.cloudhubUrl} target="_blank" rel="noopener noreferrer">{confirmation.details.cloudhubUrl}</a></span>}
              {confirmation.details.repoUrl    && <span>Repository: <a href={confirmation.details.repoUrl} target="_blank" rel="noopener noreferrer">{confirmation.details.repoUrl}</a></span>}
              {confirmation.details.branch     && <span>Branch: <strong>{confirmation.details.branch}</strong></span>}
            </div>
          )}
          {confirmation.details && confirmation.details.links && Object.keys(confirmation.details.links).length > 0 && (
            <div className="sh-deploy-confirmation-links">
              <span className="sh-deploy-links-label">Quick Links:</span>
              {Object.entries(confirmation.details.links).map(function(entry) {
                return (
                  <a key={entry[0]} href={entry[1]} target="_blank" rel="noopener noreferrer" className="sh-deploy-link">
                    {entry[0].replace(/([A-Z])/g, ' $1').replace(/^./, function(s) { return s.toUpperCase(); })}
                  </a>
                );
              })}
            </div>
          )}
          <div className="sh-deploy-confirmation-actions">
            <button className="sh-deploy-proceed-btn" onClick={onProceed}>▶ Proceed</button>
            {onCancel && <button className="sh-deploy-cancel-btn" onClick={onCancel}>✕ Cancel</button>}
          </div>
        </div>
      )}

    </div>
  );
}

export default DeploymentWindow;
